import { apiNetworking } from 'services/apiService';

export interface GraphNode {
  id: string;
  name: string;
  photo_url: string;
  size: number;
  type: 'USER' | 'COMPANY' | 'ORGANIZATION' | string;
}

export interface GraphEdge {
  source: string;
  target: string;
  type: string;
  value: number;
}

export interface GraphResponse {
  nodes: GraphNode[];
  edges: GraphEdge[];
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

export interface FollowersResponse extends PaginatedResponse<GraphNode> {}
export interface FollowingResponse extends PaginatedResponse<GraphNode> {}

export interface PaginationParams {
  page?: number;
  size?: number;
}

const validatePaginationParams = (params: PaginationParams): PaginationParams => {
  const { page = 1, size = 50 } = params;

  const validPage = Math.max(1, page);
  const validSize = Math.min(100, Math.max(1, size));

  return {
    page: validPage,
    size: validSize,
  };
};

const buildPaginationQuery = (params: PaginationParams): string => {
  const { page, size } = validatePaginationParams(params);
  return `?page=${page}&size=${size}`;
};

// Função auxiliar para tratamento de erros
const handleApiError = (error: unknown, operation: string): never => {
  console.error(`Erro na operação ${operation}:`, error);

  if (error && typeof error === 'object' && 'response' in error) {
    // Erro da API com resposta
    const apiError = error as { response: { status: number; data?: { message?: string } } };
    const { status, data } = apiError.response;
    throw new Error(`Erro ${status}: ${data?.message || 'Erro na requisição'}`);
  } else if (error && typeof error === 'object' && 'request' in error) {
    // Erro de rede
    throw new Error('Erro de conexão. Verifique sua internet.');
  } else {
    // Erro geral
    const errorMessage = error instanceof Error ? error.message : String(error);
    throw new Error(`Erro inesperado: ${errorMessage}`);
  }
};

export const getGraph = async (): Promise<GraphResponse> => {
  try {
    const { data } = await apiNetworking.get('/graph/');
    return data;
  } catch (error) {
    return handleApiError(error, 'getGraph');
  }
};

export const getIndividualGraph = async (start_node: string): Promise<GraphResponse> => {
  if (!start_node || typeof start_node !== 'string') {
    throw new Error('start_node deve ser uma string válida');
  }

  try {
    const { data } = await apiNetworking.get(`/graph/${start_node}`);
    return data;
  } catch (error) {
    return handleApiError(error, 'getIndividualGraph');
  }
};

export const getShortestPath = async (start_node: string): Promise<GraphResponse> => {
  if (!start_node || typeof start_node !== 'string') {
    throw new Error('start_node deve ser uma string válida');
  }

  try {
    const { data } = await apiNetworking.get(`/graph/${start_node}/shortest-paths`);
    return data;
  } catch (error) {
    return handleApiError(error, 'getShortestPath');
  }
};

export const getShortestPathLength = async (start_node: string): Promise<GraphResponse> => {
  if (!start_node || typeof start_node !== 'string') {
    throw new Error('start_node deve ser uma string válida');
  }

  try {
    const { data } = await apiNetworking.get(`/graph/${start_node}/shortest-path-lengths`);
    return data;
  } catch (error) {
    return handleApiError(error, 'getShortestPathLength');
  }
};

export const getFollowers = async (
  node_uuid: string,
  paginationParams: PaginationParams = {}
): Promise<FollowersResponse> => {
  if (!node_uuid || typeof node_uuid !== 'string') {
    throw new Error('node_uuid deve ser uma string válida');
  }

  try {
    const queryString = buildPaginationQuery(paginationParams);
    const { data } = await apiNetworking.get(`/graph/${node_uuid}/followers${queryString}`);
    return data;
  } catch (error) {
    return handleApiError(error, 'getFollowers');
  }
};

export const getFollowing = async (
  node_uuid: string,
  paginationParams: PaginationParams = {}
): Promise<FollowingResponse> => {
  if (!node_uuid || typeof node_uuid !== 'string') {
    throw new Error('node_uuid deve ser uma string válida');
  }

  try {
    const queryString = buildPaginationQuery(paginationParams);
    const { data } = await apiNetworking.get(`/graph/${node_uuid}/following${queryString}`);
    return data;
  } catch (error) {
    return handleApiError(error, 'getFollowing');
  }
};
